import { memo, useCallback } from 'react';
import { Button, Input } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

/**
 * Step 2 of the bulk Remove: type the confirmation word; the red button stays disabled until it
 * matches. Back returns to step 1.
 *
 * @param {object} props
 * @param {string} props.word The word the user has to type.
 * @param {number} props.count Members that will be removed.
 * @param {string} props.value Typed text.
 * @param {boolean} props.isMatch
 * @param {boolean} props.isBusy Removing: everything is locked.
 * @param {(value: string) => void} props.onChange
 * @param {() => void} props.onBack
 * @param {() => void} props.onConfirm
 */
const MembersRemoveStep2 = ({ word, count, value, isMatch, isBusy, onChange, onBack, onConfirm }) => {
  const { t } = useTranslation('people-access');
  const handleChange = useCallback((event) => onChange(event.target.value), [onChange]);
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && isMatch && !isBusy) onConfirm();
    },
    [isMatch, isBusy, onConfirm],
  );

  return (
    <>
      <div className="px-6 py-4">
        <Input
          aria-label={t('members.remove.typeLabel', { word })}
          placeholder={t('members.remove.typePlaceholder', { word })}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isBusy}
          autoComplete="off"
          autoFocus
        />
      </div>
      <div className="flex justify-end gap-2 px-6 py-4">
        <Button variant="secondary" size="md" onClick={onBack} disabled={isBusy}>
          {t('members.remove.back')}
        </Button>
        <Button variant="danger" size="md" onClick={onConfirm} disabled={!isMatch || isBusy} loading={isBusy}>
          {t('members.remove.confirm', { count })}
        </Button>
      </div>
    </>
  );
};

export default memo(MembersRemoveStep2);
